/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
    Scale, Globe, FileText, Clock,
    MessageSquare, BookOpen, Settings,
    Loader2
} from 'lucide-react';
import Sidebar from '../chat/Sidebar';
import ChatHeader from '../chat/ChatHeader';
import ChatMessages from '../chat/ChatMessages';
import ChatInput from '../chat/ChatInput';
import PlansModal from '../chat/PlansModal';
import api from '../../utils/api';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

export const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
        deviceId = 'guest_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
};

export default function LawyerAI() {
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [conversationId, setConversationId] = useState(null);
    const [remainingMessages, setRemainingMessages] = useState(null);
    const [showPlans, setShowPlans] = useState(false);
    const [plans, setPlans] = useState([]);
    const [isGuest, setIsGuest] = useState(true);
    const [user, setUser] = useState(null);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);


    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const toggleVoice = () => {
        if (!recognition) {
            Swal.fire(t('chatbot.swal.not_supported_title'), t('chatbot.swal.not_supported_text'), "warning");
            return;
        }

        if (isListening) {
            recognition.stop();
        } else {
            recognition.start();
            setIsListening(true);
        }
    };

    useEffect(() => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            console.warn('Speech Recognition not supported');
            return;
        }

        const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
        const recog = new SpeechRecognition();

        recog.continuous = false;
        recog.interimResults = true;
        recog.lang = 'en-US';

        recog.onresult = (event) => {
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript + ' ';
                } else {
                    interimTranscript += transcript;
                }
            }

            const fullText = (finalTranscript + interimTranscript).trim();
            setInput(fullText);

            if (event.results[event.results.length - 1].isFinal && finalTranscript.trim()) {
                setTimeout(() => {
                    if (!isLoading && input.trim()) {
                        sendMessage();
                    }
                }, 800);
            }
        };

        recog.onerror = (event) => {
            console.error('Voice error:', event.error);
            setIsListening(false);
            Swal.fire(t('chatbot.swal.voice_error_title'), t('chatbot.swal.voice_error_text'), "error");
        };

        recog.onend = () => {
            setIsListening(false);
        };

        setRecognition(recog);
    }, []);

    useLayoutEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        const authUser = localStorage.getItem('auth_user');

        if (token && authUser) {
            setIsGuest(false);
            setUser(JSON.parse(authUser));
        } else {
            setIsGuest(true);
        }

        startConversation();
    }, []);

    const startConversation = async () => {
        setIsLoading(true);
        try {
            const headers = {};
            const token = localStorage.getItem('auth_token');
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            } else {
                headers['X-Device-ID'] = getDeviceId();
            }

            const res = await api.get('/chat/conversation', { headers });

            if (res.data.success) {
                const conv = res.data.data.conversation;
                setConversationId(conv.id);
                setRemainingMessages(conv.remaining_messages ?? 3);
                setMessages(res.data.data.messages.map(msg => ({
                    id: msg.id,
                    role: msg.role,
                    content: msg.content,
                    timestamp: new Date(msg.created_at)
                })));
            }
        } catch (err) {
            console.error("Start conversation error:", err.response);
            Swal.fire(t('chatbot.swal.error_title'), t('chatbot.swal.start_chat_error'), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!input.trim() || isLoading || !conversationId) return;

        if (remainingMessages !== null && remainingMessages <= 0) {
            showLimitPopup();
            return;
        }

        const userMessage = { role: 'user', content: input, timestamp: new Date() };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const headers = {};
            const token = localStorage.getItem('auth_token');
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            } else {
                headers['X-Device-ID'] = getDeviceId();
            }

            const res = await api.post('/chat/message', {
                conversation_id: conversationId,
                message: input
            }, { headers });

            if (res.data.success) {
                const msg = res.data.data.message;
                const newMsg = {
                    id: msg.id,
                    role: msg.role,
                    content: msg.content,
                    timestamp: new Date(msg.created_at)
                };
                setMessages(prev => [...prev, newMsg]);

                // Update remaining messages safely
                if (res.data.data.remaining_messages !== undefined) {
                    setRemainingMessages(res.data.data.remaining_messages);

                    if (res.data.data.remaining_messages <= 0) {
                        setTimeout(() => showLimitPopup(), 800);
                    }
                } else {
                    // Fallback: manually decrement if API doesn't return the count
                    setRemainingMessages(prev => {
                        const newVal = (prev !== null ? prev : 3) - 1;
                        if (newVal <= 0) {
                            setTimeout(() => showLimitPopup(), 800);
                        }
                        return newVal;
                    });
                }
            }
        } catch (err) {
            const errors = err.response?.data?.errors;
            if (errors) {
                let msg = t('chatbot.swal.validation_failed') + "\n";
                Object.values(errors).forEach(e => msg += `• ${e[0]}\n`);
                Swal.fire(t('chatbot.swal.error_title'), msg, "error");
            } else {
                Swal.fire(t('chatbot.swal.error_title'), t('chatbot.swal.send_message_error'), "error");
            }
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setIsLoading(false);
        }
    };

    // === Show limit popup ===
    const showLimitPopup = async () => {
        if (!isGuest) {
            await fetchPlans();
            setShowPlans(true);
        } else {
            const result = await Swal.fire({
                title: t('chatbot.swal.limit_reached_title'),
                text: t('chatbot.swal.limit_reached_text'),
                icon: "info",
                showCancelButton: true,
                confirmButtonText: t('chatbot.swal.login'),
                cancelButtonText: t('chatbot.swal.view_plans')
            });

            if (result.isConfirmed) {
                window.location.href = '/login';
            } else {
                await fetchPlans();
                setShowPlans(true);
            }
        }
    };

    // === Fetch plans ===
    const fetchPlans = async () => {
        try {
            const res = await api.get('/chat/plans');
            if (res.data.success) {
                setPlans(res.data.data.plans);
            }
        } catch (err) {
            console.error("Failed to load plans:", err);
        }
    };

    // === Checkout ===
    const checkout = async (planId) => {
        const handleLoginRedirect = () => {
            Swal.fire({
                title: t('chatbot.swal.checkout_failed_title'),
                text: t('chatbot.swal.checkout_failed_text'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t('chatbot.swal.login'),
                cancelButtonText: t('chatbot.swal.cancel')
            }).then((result) => {
                if (result.isConfirmed) {
                    window.location.href = '/login';
                }
            });
        };

        if (isGuest) {
            handleLoginRedirect();
            return;
        }

        try {
            const res = await api.post('/chat/checkout', { plan_id: planId });
            if (res.data.success) {
                window.location.href = res.data.data.checkout_url;
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                handleLoginRedirect();
            } else {
                Swal.fire(t('chatbot.swal.error_title'), t('chatbot.swal.checkout_error'), "error");
            }
        }
    };

    const quickActions = [
        { icon: <Globe className="w-5 h-5" />, text: t('chatbot.quick_actions.immigration'), query: t('chatbot.quick_actions.immigration_query') },
        { icon: <FileText className="w-5 h-5" />, text: t('chatbot.quick_actions.documents'), query: t('chatbot.quick_actions.documents_query') },
        { icon: <Clock className="w-5 h-5" />, text: t('chatbot.quick_actions.processing'), query: t('chatbot.quick_actions.processing_query') }
    ];

    const practiceAreas = [
        { icon: <Scale className="w-6 h-6" />, title: t('chatbot.practice_areas.general_law'), desc: t('chatbot.practice_areas.general_law_desc') },
        { icon: <Globe className="w-6 h-6" />, title: t('chatbot.practice_areas.immigration'), desc: t('chatbot.practice_areas.immigration_desc') },
        { icon: <FileText className="w-6 h-6" />, title: t('chatbot.practice_areas.legal_docs'), desc: t('chatbot.practice_areas.legal_docs_desc') },
        { icon: <BookOpen className="w-6 h-6" />, title: t('chatbot.practice_areas.research'), desc: t('chatbot.practice_areas.research_desc') }
    ];

    const navItems = [
        { icon: <MessageSquare className="w-5 h-5" />, label: t('chatbot.nav.ai_chat'), active: true },
        { icon: <BookOpen className="w-5 h-5" />, label: t('chatbot.nav.knowledge_base'), active: false },
        { icon: <Settings className="w-5 h-5" />, label: t('chatbot.nav.settings'), active: false }
    ];

    return (
        <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 overflow-hidden">
            <Sidebar
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                navItems={navItems}
                practiceAreas={practiceAreas}
                quickActions={quickActions}
                handleQuickAction={(q) => { setInput(q); inputRef.current?.focus(); }}
                remainingMessages={remainingMessages}
                isGuest={isGuest}
            />

            <div className="flex-1 flex flex-col">
                <ChatHeader
                    setSidebarOpen={setSidebarOpen}
                    remainingMessages={remainingMessages}
                    isGuest={isGuest}
                    user={user}
                    onUpgrade={() => {
                        fetchPlans();
                        setShowPlans(true);
                    }}
                />

                {isLoading && messages.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : (
                    <>
                        <ChatMessages messages={messages} isLoading={isLoading} />
                        <div ref={messagesEndRef} className="h-0" />
                        <ChatInput
                            input={input}
                            setInput={setInput}
                            handleSend={sendMessage}
                            isLoading={isLoading}
                            inputRef={inputRef}
                            disabled={remainingMessages === 0}
                            isListening={isListening}
                            toggleVoice={toggleVoice}
                            recognition={recognition}
                        />
                    </>
                )}
            </div>

            <PlansModal
                isOpen={showPlans}
                onClose={() => setShowPlans(false)}
                plans={plans}
                onCheckout={checkout}
            />

            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
}